/**
 * 공통 코드 API
 */
import { AxiosError } from "axios";
import { apiClient } from "@/shared/api/client";
import { GetCommonCodeListResponseDto } from "@/shared/types/commonCode.types";
import {
    CommonCodeApiResponse,
    GetCommonCodeGroupListResponseDto
} from "../types/systemCommonCode.types";

export const commonCodeApi = {
    /**
     * 공통코드 조회 (공통코드 관리 페이지 진입 시)
     * GET /api/v1/portal/system/common-code
     * @param grpCode - 선택적 그룹코드 (쿼리 파라미터)
     */
    getCommonCode: async (grpCode?: string): Promise<CommonCodeApiResponse> => {
        const url = grpCode
            ? `/api/v1/portal/system/common-code?grpCode=${grpCode}`
            : '/api/v1/portal/system/common-code';
        const response = await apiClient.get<CommonCodeApiResponse>(url);
        return response.data;
    },

    /**
     * 공통코드 그룹 목록 조회 (vr_comm_grp)
     */
    getCommonCodeGroupList: async (): Promise<GetCommonCodeGroupListResponseDto> => {
        const response = await apiClient.get<GetCommonCodeGroupListResponseDto>(
            '/api/v1/common-code/group/list'
        );
        return response.data;
    },

    /**
     * 그룹코드별 공통코드 목록 조회 (vr_comm_code)
     */
    getCommonCodeList: async (grpCode: string): Promise<GetCommonCodeListResponseDto> => {
        const response = await apiClient.get<GetCommonCodeListResponseDto>(
            '/api/v1/common-code/list',
            { params: { grpCode } }
        );
        return response.data;
    },

    /**
     * 그룹코드 등록/수정
     * POST /api/v1/portal/system/common-code/group
     * @param data - 그룹코드 등록/수정 데이터
     * @returns 성공 여부 및 변경사항 여부
     */
    saveCommonCodeGroup: async (data: {
        userId: string;
        grpCode: string;
        grpName: string;
        grpVal?: string;
        seqNo?: number;
    }): Promise<{ success: boolean; message?: string; hasChanges?: boolean }> => {
        try {
            const response = await apiClient.post(
                '/api/v1/portal/system/common-code/group',
                data
            );
            return {
                success: true,
                message: response.data?.message || '그룹코드가 저장되었습니다.',
                hasChanges: response.data?.hasChanges !== false // 기본값은 true, 명시적으로 false일 때만 false
            };
        } catch (error: unknown) {
            // 에러 응답에서 메시지 추출
            let errorMessage = '그룹코드 저장에 실패했습니다.';

            if (error instanceof Error) {
                errorMessage = error.message;
            }

            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as AxiosError<{ message?: string }>;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                }
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    },

    /**
     * 공통코드 등록/수정
     * POST /api/v1/portal/system/common-code/code
     * @param data - 공통코드 등록/수정 데이터
     * @returns 성공 여부 및 변경사항 여부
     */
    saveCommonCode: async (data: {
        userId: string;
        grpCode: string;
        comCode: string;
        comCdName: string;
        comSeqno?: string;
        seqNo?: number;
        useYn: string;
        val1?: string;
        val2?: string;
        val3?: string;
        val4?: string;
        val5?: string;
    }): Promise<{ success: boolean; message?: string; hasChanges?: boolean }> => {
        try {
            const response = await apiClient.post(
                '/api/v1/portal/system/common-code/code',
                data
            );
            return {
                success: true,
                message: response.data?.message || '공통코드가 저장되었습니다.',
                hasChanges: response.data?.hasChanges !== false // 기본값은 true, 명시적으로 false일 때만 false
            };
        } catch (error: unknown) {
            // 에러 응답에서 메시지 추출
            let errorMessage = '공통코드 저장에 실패했습니다.';

            if (error instanceof Error) {
                errorMessage = error.message;
            }

            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as AxiosError<{ message?: string }>;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                }
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    }
};
